import type {
  CommitmentRenewalBehavior,
  EarlyExitCharge,
} from "@/server-types";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { t } from "@lingui/core/macro";
import type { UseFormReturnType } from "@mantine/form";
import { useAvailableFiscalizationTaxSystems } from "@/hooks/useAvailableFiscalizationTaxSystems";
import { getTaxPercentageItems } from "@/utils/taxPercentages";
import { PolicySettingsSection } from "./PolicySettingsSection";

export type CommitmentPolicyFieldValues = {
  renewalBehavior: CommitmentRenewalBehavior;
  earlyExitCharge: EarlyExitCharge;
  /** Euros in the form; the settings component converts to cents on the wire. */
  earlyExitFlatFee: number;
  minimumPaidPeriods: number;
  /** Null until the salon picks one, which blocks issuing rather than guessing a rate. */
  earlyExitTaxPercentage: number | null;
};

export function renewalBehaviorItems() {
  return [
    {
      key: "rollToRolling",
      label: t`Nadaljuje se brez vezave`,
      description: t`Paket se obnavlja po isti ceni, stranka pa ga lahko kadar koli odpove.`,
    },
    {
      key: "renewSameTerm",
      label: t`Vezava se samodejno podaljša`,
      description: t`Začne se nova vezava z enakim številom obdobij in po isti ceni.`,
    },
    {
      key: "expire",
      label: t`Paket se zaključi`,
      description: t`Paket se ne obnavlja več in preneha veljati ob koncu zadnjega obdobja vezave.`,
    },
  ];
}

export function earlyExitChargeItems() {
  return [
    {
      key: "none",
      label: t`Ne plača nič`,
      description: t`Stranka lahko vezavo prekine brez dodatnih stroškov.`,
    },
    {
      key: "flatFee",
      label: t`Plača fiksen znesek`,
      description: t`Ne glede na to, kdaj odpove, stranka plača znesek, ki ga določite spodaj.`,
    },
    {
      key: "savingClawback",
      label: t`Plača razliko do cene brez vezave`,
      description: t`Za vsa pretekla obdobja vezave stranka doplača razliko med ceno brez vezave in ceno z vezavo.`,
    },
    {
      key: "minimumPeriodsFloor",
      label: t`Plača vsaj določeno število obdobij`,
      description: t`Če je do odpovedi minilo manj obdobij, kot jih določite spodaj, stranka doplača manjkajoča. Npr. najmanj 6, odpove po 4 obdobjih: plača še 2.`,
    },
    {
      key: "remainingPeriods",
      label: t`Plača vsa preostala obdobja vezave`,
      description: t`Stranka plača vsa obdobja do konca vezave po ceni z vezavo. Npr. 12 obdobij, odpove po 4: plača še 8.`,
    },
  ];
}

/**
 * Generic because `UseFormReturnType` is invariant: a plain
 * `UseFormReturnType<CommitmentPolicyFieldValues>` prop would reject the override form.
 */
type CommitmentPolicyFormFieldsProps<T extends CommitmentPolicyFieldValues> = {
  form: UseFormReturnType<T>;
};

export function CommitmentPolicyFormFields<
  T extends CommitmentPolicyFieldValues,
>({ form }: CommitmentPolicyFormFieldsProps<T>) {
  const charge = form.values.earlyExitCharge;
  const renewalItems = renewalBehaviorItems();
  const chargeItems = earlyExitChargeItems();
  const { availableTaxSystems } = useAvailableFiscalizationTaxSystems();
  // One account bills under one tax system, so its rates are the only ones on offer.
  const taxItems = getTaxPercentageItems(availableTaxSystems[0] ?? "furs");
  // `setFieldValue` resolves its value type through the generic and loses the
  // enum, so the two selects go through one narrowed setter instead.
  const setValue = form.setFieldValue as (path: string, value: string) => void;
  const setTaxPercentage = form.setFieldValue as (
    path: string,
    value: number,
  ) => void;

  return (
    <div className="flex flex-col gap-5">
      <PolicySettingsSection
        label={t`PO IZTEKU VEZAVE`}
        description={t`Kaj se zgodi s paketom, ko se izteče zadnje obdobje, na katero se je stranka vezala.`}
      >
        <LimeSelect
          label={t`Paket po izteku vezave`}
          items={renewalItems}
          value={form.values.renewalBehavior}
          onSingleItemChange={(value) => setValue("renewalBehavior", value)}
          description={
            renewalItems.find(
              (item) => item.key === form.values.renewalBehavior,
            )?.description
          }
          data-identifier="commitment-policy-renewal-behavior"
        />
      </PolicySettingsSection>

      <PolicySettingsSection
        label={t`PREDČASNA ODPOVED`}
        description={t`Koliko mora stranka doplačati, če paket odpove pred koncem vezave. Znesek vam ob odpovedi le predlagamo, vi pa ga potrdite ali odpišete.`}
      >
        <LimeSelect
          label={t`Stranka ob predčasni odpovedi`}
          items={chargeItems}
          value={charge}
          onSingleItemChange={(value) => setValue("earlyExitCharge", value)}
          description={
            chargeItems.find((item) => item.key === charge)?.description
          }
          data-identifier="commitment-policy-early-exit-charge"
        />

        {charge === "flatFee" && (
          <LimeNumberInput
            size="sm"
            label={t`Znesek`}
            placeholder={t`Npr. 50`}
            minValue={0}
            data-identifier="commitment-policy-flat-fee"
            {...form.getInputProps("earlyExitFlatFee")}
          />
        )}

        {charge === "minimumPeriodsFloor" && (
          <LimeNumberInput
            size="sm"
            label={t`Najmanjše število obdobij`}
            placeholder={t`Npr. 6`}
            minValue={0}
            step={1}
            data-identifier="commitment-policy-minimum-paid-periods"
            {...form.getInputProps("minimumPaidPeriods")}
          />
        )}

        {charge !== "none" && (
          <LimeSelect
            label={t`DDV za strošek predčasne odpovedi`}
            description={t`Uporabi se, ko strošek izdate kot predračun ali račun. Dokumenta ni mogoče izdati, dokler stopnja ni izbrana.`}
            items={taxItems}
            value={
              form.values.earlyExitTaxPercentage == null
                ? null
                : String(form.values.earlyExitTaxPercentage)
            }
            onSingleItemChange={(value) =>
              setTaxPercentage("earlyExitTaxPercentage", Number(value))
            }
            data-identifier="commitment-policy-early-exit-tax"
          />
        )}
      </PolicySettingsSection>
    </div>
  );
}
